<template>
  <div class="home">
    <section v-if="hero" :class="['home__hero', { 'is-bg-ready': heroBackgroundReady }]" :style="heroStyle">
      <div class="home__hero-overlay">
        <p class="home__hero-eyebrow">{{ hero.eyebrow }}</p>
        <h2>{{ hero.title }}</h2>
        <p class="home__hero-subtitle">{{ hero.subtitle }}</p>
        <div class="home__hero-actions">
          <router-link v-if="hero.primary" class="home__hero-button home__hero-button--primary" :to="hero.primary.to">
            {{ hero.primary.text }}
          </router-link>
          <router-link v-if="hero.secondary" class="home__hero-button" :to="hero.secondary.to">
            {{ hero.secondary.text }}
          </router-link>
        </div>
      </div>
    </section>

    <section v-if="introParagraphs.length" class="home__intro">
      <p v-for="(paragraph, index) in introParagraphs" :key="index" v-html="paragraph" />
    </section>

    <section v-if="newsItems.length" class="home__news">
      <div class="home__section-header">
        <h3 class="heading-with-bar">{{ news.heading }}</h3>
        <!-- <router-link class="home__section-link" to="/research">
          {{ news.more }}
        </router-link> -->
      </div>
      <ul class="home__news-list">
        <li v-for="item in newsItems" :key="item.title">
          <span class="home__news-date">{{ item.date }}</span>
          <div>
            <p class="home__news-title" v-html="item.title"></p>
            <p v-if="item.summary" class="home__news-summary" v-html="item.summary"></p>
          </div>
        </li>
      </ul>
    </section>

    <section v-if="researchItems.length" class="home__research">
      <div class="home__research-header">
        <div class="home__research-heading">
          <div class="home__research-title">
            <h3 class="heading-with-bar">{{ research.heading }}</h3>
          </div>
        </div>
        <router-link class="home__section-link home__section-link--light" to="/publications">
          {{ research.more }}
        </router-link>
      </div>

      <div class="home__research-layout">
        <div class="home__research-grid">
          <router-link v-for="item in researchItems" :key="item.publicationId || item.id || item.title"
            class="home__research-card" :to="getResearchLink(item)">
            <div :class="[
              'home__research-media',
              {
                'home__research-media--loading':
                  !isImageReady(researchId(item)) && !hasImageError(researchId(item)),
                'home__research-media--error': hasImageError(researchId(item)),
              },
            ]">
              <img v-lazy="item.image" :alt="item.title" loading="lazy"
                :class="{ 'is-visible': isImageReady(researchId(item)) }" @load="handleImageLoad(researchId(item))"
                @error="handleImageError(researchId(item))" />
            </div>
            <div class="home__research-meta">
              <span v-if="item.type" class="home__pill">{{ item.type }}</span>
              <span v-if="item.year" class="home__dot"></span>
              <span v-if="item.year" class="home__meta-text">{{ item.year }}</span>
            </div>
            <h4>{{ item.title }}</h4>
            <p v-if="item.venue" class="home__research-venue">{{ item.venue }}</p>
          </router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

export default {
  name: 'HomeView',
  components: {
  },
  data() {
    return {
      imageStates: {},
      heroBackgroundReady: false,
      heroBackgroundUrl: '',
    };
  },
  methods: {
    getResearchLink(item) {
      const publicationId = item?.publicationId || item?.id;
      if (publicationId) {
        return { name: 'PublicationDetail', params: { id: publicationId } };
      }
      return { name: 'Publications' };
    },
    researchId(item) {
      return item?.publicationId || item?.id || item?.title || '';
    },
    extractYear(yearValue) {
      const match = String(yearValue || '').match(/(\d{4})/);
      return match ? match[1] : yearValue || '';
    },
    isImageReady(id) {
      return this.imageStates[id] === 'loaded';
    },
    hasImageError(id) {
      return this.imageStates[id] === 'error';
    },
    handleImageLoad(id) {
      this.$set(this.imageStates, id, 'loaded');
    },
    handleImageError(id) {
      this.$set(this.imageStates, id, 'error');
    },
    prepareHeroBackground(url) {
      this.heroBackgroundReady = false;
      this.heroBackgroundUrl = '';

      if (!url) return;

      const img = new Image();
      img.onload = () => {
        this.heroBackgroundReady = true;
        this.heroBackgroundUrl = url;
      };
      img.onerror = () => {
        this.heroBackgroundReady = false;
        this.heroBackgroundUrl = '';
      };
      img.src = url;
    },
  },
  computed: {
    hero() {
      return getLocaleBlock(this.$i18n, 'home.hero', {}) || null;
    },
    heroStyle() {
      const gradient = 'linear-gradient(rgba(8, 18, 36, 0.45), rgba(8, 18, 36, 0.55))';
      return {
        '--hero-gradient': gradient,
        '--hero-background': this.heroBackgroundReady && this.heroBackgroundUrl
          ? `url(${this.heroBackgroundUrl})`
          : 'none',
      };
    },
    introParagraphs() {
      const paragraphs = getLocaleBlock(this.$i18n, 'home.intro.paragraphs', []);
      return Array.isArray(paragraphs) ? paragraphs : [];
    },
    news() {
      return getLocaleBlock(this.$i18n, 'home.news', {}) || {};
    },
    newsItems() {
      const items = this.news?.items || [];
      return Array.isArray(items) ? items : [];
    },
    research() {
      return getLocaleBlock(this.$i18n, 'home.research', {}) || {};
    },
    publications() {
      const list = getLocaleBlock(this.$i18n, 'publicationsPage.publications', []);
      return Array.isArray(list) ? list : [];
    },
    latestPublications() {
      const isPatent = (publication) => {
        const typeValue = String((publication && publication.type) || '').toLowerCase();
        return typeValue.includes('专利') || typeValue.includes('patent');
      };

      const toYearMonth = (value) => {
        if (typeof value === 'number') {
          return value * 100;
        }
        const match = String(value || '').match(/(\d{4})\D*(\d{1,2})?/);
        if (!match) return 0;
        const year = Number(match[1]);
        const month = Number(match[2] || '1');
        if (Number.isNaN(year) || Number.isNaN(month)) return 0;
        return year * 100 + month;
      };

      return this.publications
        .map((publication, index) => ({ ...publication, __index: index }))
        .filter((publication) => !isPatent(publication))
        .sort((a, b) => {
          const yearDiff = toYearMonth(b.year) - toYearMonth(a.year);
          if (yearDiff !== 0) {
            return yearDiff;
          }
          return a.__index - b.__index;
        })
        .slice(0, 4)
        .map((publication) => ({
          id: publication.id,
          publicationId: publication.id,
          title: publication.title,
          image: publication.thumbnail || publication.image || this.hero?.backgroundImage || '',
          venue: publication.venue || '',
          type: publication.type || '',
          year: this.extractYear(publication.year),
          topic: Array.isArray(publication.topics) ? publication.topics[0] : '',
        }));
    },
    researchItems() {
      if (this.latestPublications.length) {
        return this.latestPublications;
      }
      const items = this.research?.items || [];
      return Array.isArray(items) ? items : [];
    },
    teamMembers() {
      const items = getLocaleBlock(this.$i18n, 'home.teamHighlight.members', []);
      return Array.isArray(items) ? items : [];
    },
  },
  watch: {
    hero: {
      immediate: true,
      handler(newHero) {
        this.prepareHeroBackground(newHero?.backgroundImage);
      },
    },
  },
};
</script>

<style scoped>
.home {
  display: grid;
  gap: var(--spacing-xl);
  max-width: 1400px;
  margin-right: auto;
  margin-left: auto;
}

/* Hero Section (Keep new style) */
.home__hero {
  position: relative;
  border-radius: 1.5rem;
  /* Consistent rounding */
  min-height: 460px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark));
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  box-shadow: var(--box-shadow-lg);
  --hero-gradient: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.5));
  --hero-background: none;
}

.home__hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--hero-gradient), var(--hero-background);
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1);
  transition: opacity 0.55s ease, transform 1.2s ease-out;
  pointer-events: none;
}

.home__hero.is-bg-ready::before {
  opacity: 1;
}

.home__hero:hover::before {
  transform: scale(1.1);
}

.home__hero-overlay {
  padding: var(--spacing-xl);
  color: var(--text-color-on-primary);
  max-width: 640px;
  position: relative;
  z-index: 1;
}

.home__hero-eyebrow {
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: var(--font-size-sm);
  opacity: 0.8;
  margin-bottom: var(--spacing-sm);
}

.home__hero h2 {
  font-size: clamp(2.4rem, 3.4vw, 3.2rem);
  line-height: 1.15;
  margin-bottom: var(--spacing-md);
  color: var(--text-color-on-primary);
}

.home__hero-subtitle {
  font-size: var(--font-size-lg);
  line-height: var(--line-height-base);
  margin-bottom: var(--spacing-lg);
  color: rgba(255, 255, 255, 0.9);
}

.home__hero-actions {
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.home__hero-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  font-weight: var(--font-weight-bold);
  color: var(--text-color-on-primary);
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.15);
  transition: all 0.2s ease;
}

.home__hero-button--primary {
  background: var(--surface-color);
  color: var(--primary-color);
  border-color: transparent;
  box-shadow: var(--box-shadow);
}

.home__hero-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--box-shadow-lg);
}

.home__hero-button--primary:hover {
  background: var(--background-color);
}

/* Reverted Section Card Styles */
.home__intro {
  background: var(--surface-color);
  padding: 2.2rem;
  border-radius: 28px;
  box-shadow: 0 24px 60px rgba(18, 32, 62, 0.12);
  border: 1px solid rgba(32, 58, 110, 0.08);
  display: grid;
  gap: 1rem;
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-color-light);
}

.home__news {
  background: var(--surface-color);
  padding: 2.2rem;
  border-radius: 28px;
  border: 1px solid rgba(40, 70, 132, 0.08);
  box-shadow: 0 24px 50px rgba(20, 40, 78, 0.1);
}

.home__research {
  position: relative;
  background: var(--surface-color);
  padding: 2.2rem;
  border-radius: 28px;
  border: 1px solid rgba(40, 70, 132, 0.08);
  box-shadow: 0 24px 50px rgba(20, 40, 78, 0.1);
  overflow: hidden;
  color: var(--text-color);
}


.home__intro strong {
  color: var(--text-color);
}

/* Section Headers */
.home__section-header,
.home__research-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.home__section-header h3,
.home__research-title h3 {
  font-size: 1.75rem;
  color: var(--text-color);
  margin: 0;
}

.home__section-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: var(--font-weight-bold);
}

.home__section-link:hover {
  color: var(--primary-color-dark);
}

/* News Section */
.home__news-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--spacing-md);
}

.home__news-list li {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: var(--spacing-md);
}

.home__news-list li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.home__news-date {
  font-weight: var(--font-weight-bold);
  color: var(--text-color-light);
}

.home__news-title {
  margin: 0;
  font-weight: 500;
  color: var(--text-color);
  line-height: var(--line-height-base);
}

.home__news-title strong {
  font-weight: var(--font-weight-bold);
  color: var(--text-color);
}

:deep(.home__news-badge) {
  display: inline-flex;
  align-items: center;
  background-color: var(--background-color);
  color: var(--text-color);
  padding: 0.15rem 0.55rem;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin: 0 0.15rem;
  border: 1px solid var(--border-color);
}

.home__news-summary {
  margin: 0.4rem 0 0;
  color: var(--text-color-light);
  line-height: var(--line-height-base);
}


/* Research Section */
.home__research-grid {
  display: grid;
  gap: 1.6rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
}

/* Reverted research card style */
.home__research-card {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: var(--surface-color);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(18, 36, 60, 0.06);
  box-shadow: 0 10px 24px rgba(20, 40, 78, 0.12);
  padding: 0.85rem 0.85rem 1.1rem;
  color: var(--text-color);
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.home__research-card:hover {
  transform: translateY(-3px);
  border-color: rgba(18, 36, 60, 0.16);
  box-shadow: 0 16px 40px rgba(20, 40, 78, 0.18);
}


.home__research-media {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  position: relative;
  background: var(--background-color);
  border-radius: 14px;
  /* border: 1px solid rgba(18, 36, 60, 0.06); */
  box-shadow: inset 0 1px 2px rgba(18, 36, 60, 0.04);
}

.home__research-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.home__research-media img.is-visible {
  opacity: 1;
}

.home__research-media--loading::after {
  content: '';
  position: absolute;
  inset: 0;
  margin: auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--primary-color-light);
  border-top-color: var(--primary-color);
  animation: spin 0.9s linear infinite;
}

.home__research-media--error {
  background: #fcf2f2;
  /* Subtle error background */
}

.home__pill {
  background-color: rgb(230, 240, 248);
  color: rgb(0, 74, 153);
  border-color: rgb(230, 240, 248) !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  text-align: center;
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  border: 1px solid;
  line-height: 1.2;
  max-width: 100%;
}

.home__research-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-xs);
}

.home__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--text-color-subtle);
}

.home__meta-text {
  color: var(--text-color-light);
  font-size: var(--font-size-sm);
}

.home__research-card h4 {
  font-size: 1.05rem;
  line-height: 1.4;
  color: var(--text-color);
  margin: 0;
  min-height: 2.8rem;
  /* 2 lines of text */
}

.home__research-venue {
  margin: 0;
  color: var(--text-color-light);
  font-size: 0.95rem;
}


@media (max-width: 900px) {
  .home__hero-overlay {
    padding: var(--spacing-lg);
  }

  .home__news-list li {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }

  .home__research,
  .home__intro,
  .home__news {
    padding: 1.6rem;
    /* Adjust padding for smaller screens */
  }

  .home__research-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
